import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft, MapPin, Globe2, Mail, Phone, BadgeCheck, ExternalLink,
  CalendarDays, Clock, Users2, Info, Instagram, Facebook, Youtube,
} from 'lucide-react';
import ClaimProfile from '../components/world/ClaimProfile';
import Flag from '../components/world/Flag';
import { useJazyk } from '../context/LanguageContext';
import { API } from '../lib/api';
import { najdiDruh } from '../lib/directoryKinds';

/**
 * Detail jednoho katalogového záznamu – organizace, akce, soutěže, rozcestníku.
 *
 * Jedna stránka pro všechny druhy: co je společné, se vykreslí vždy, a co je
 * zvláštní pro daný druh, se doplní podle `kind`.
 */

const datum = (iso, sCasem = false, jazyk = 'cs') => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString(jazyk === 'en' ? 'en-GB' : 'cs-CZ', {
      day: 'numeric', month: 'long', year: 'numeric',
      ...(sCasem ? { hour: '2-digit', minute: '2-digit' } : {}),
    });
  } catch {
    return '';
  }
};

// Formy, rozsahy a stavy ročníku jsou ve slovníku (`detail.forma.*` a spol.),
// aby se překládaly spolu se zbytkem stránky.

export default function DirectoryDetailPage() {
  const { slug, kind, id } = useParams();
  const navigate = useNavigate();
  const { jazyk, t, dvojjazycne } = useJazyk();
  const [zaznam, setZaznam] = useState(null);
  const [zeme, setZeme] = useState(null);
  const [nacita, setNacita] = useState(true);
  const [chyba, setChyba] = useState('');

  const druh = najdiDruh(kind);

  useEffect(() => {
    let zivy = true;
    setNacita(true);
    setChyba('');
    (async () => {
      try {
        const { data } = await axios.get(`${API}/directory/${kind}/${id}`);
        if (!zivy) return;
        setZaznam(data);
        const z = await axios.get(`${API}/geo/countries/${data.location.country_iso2}`)
          .catch(() => ({ data: null }));
        if (zivy) setZeme(z.data);
      } catch (e) {
        if (zivy) setChyba(e?.response?.status === 404 ? 'nenalezen' : 'chyba');
      } finally {
        if (zivy) setNacita(false);
      }
    })();
    return () => { zivy = false; };
  }, [kind, id]);

  if (nacita) {
    return <div className="min-h-[60vh] flex items-center justify-center"><div className="spinner" /></div>;
  }

  if (chyba) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-16 text-center">
        <p className="text-bloom-sub">{t(`detail.${chyba}`)}</p>
        <Link to={`/world/${slug}`} className="mt-4 inline-flex items-center gap-1.5 text-bloom-violet font-medium">
          <ArrowLeft className="w-4 h-4" /> {t('obecne.zpet')}
        </Link>
      </div>
    );
  }

  const d = zaznam.details || {};
  const odkazy = [
    ['website', zaznam.social?.website, Globe2, t('detail.web')],
    ['instagram', zaznam.social?.instagram, Instagram, 'Instagram'],
    ['facebook', zaznam.social?.facebook, Facebook, 'Facebook'],
    ['youtube', zaznam.social?.youtube, Youtube, 'YouTube'],
  ].filter(([, url]) => url);

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 py-6 sm:py-8" data-testid="directory-detail">
      <button type="button" onClick={() => navigate(`/world/${slug}`)}
              className="inline-flex items-center gap-1.5 text-sm text-bloom-sub hover:text-bloom-violet">
        <ArrowLeft className="w-4 h-4" /> {t('detail.zpetNa', {
          zeme: dvojjazycne(zeme?.name_cs, zeme?.name_en) })}
      </button>

      <header className="mt-4">
        <p className="text-sm text-bloom-violet font-medium">{t(`sekce.${kind}`)}</p>
        <h1 className="mt-1 font-heading text-2xl sm:text-4xl font-extrabold text-bloom-text flex items-start gap-2.5">
          <span>{zaznam.name}</span>
          {zaznam.verification === 'verified' && (
            <BadgeCheck className="w-6 h-6 text-bloom-mint shrink-0 mt-1.5" />
          )}
        </h1>

        <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-bloom-sub">
          {zeme && (
            <Link to={`/world/${zeme.slug}`} className="inline-flex items-center gap-1.5 hover:text-bloom-violet">
              <Flag emoji={zeme.flag} iso2={zeme.iso2} size="sm" />
              {dvojjazycne(zeme.name_cs, zeme.name_en)}
            </Link>
          )}
          {zaznam.location?.city_name && (
            <span className="inline-flex items-center gap-1.5">
              <MapPin className="w-3.5 h-3.5" />{zaznam.location.city_name}
            </span>
          )}
          {kind === 'events' && d.format && (
            <span className="inline-flex items-center gap-1.5">
              <Users2 className="w-3.5 h-3.5" />{t(`detail.forma.${d.format}`)}
            </span>
          )}
          {kind === 'pageants' && d.scope && (
            <span>{t(`detail.rozsah.${d.scope}`)}</span>
          )}
        </div>
      </header>

      {/* Termíny – u akcí a soutěží to je to nejdůležitější, tak hned nahoře */}
      {(d.starts_at || d.registration_deadline) && (
        <div className="mt-6 grid gap-3 sm:grid-cols-2">
          {d.starts_at && (
            <Udaj icon={CalendarDays} popisek={t(kind === 'pageants' ? 'detail.termin' : 'detail.zacatek')}
                  hodnota={datum(d.starts_at, kind === 'events', jazyk)} />
          )}
          {d.ends_at && <Udaj icon={Clock} popisek={t('detail.konec')} hodnota={datum(d.ends_at, true, jazyk)} />}
          {d.registration_deadline && (
            <Udaj icon={Clock} popisek={t('detail.uzaverka')} hodnota={datum(d.registration_deadline, false, jazyk)} />
          )}
          {kind === 'pageants' && d.edition_status && (
            <Udaj icon={Info} popisek={t('detail.stavRocniku')}
                  hodnota={t(`detail.rocnik.${d.edition_status}`)} />
          )}
        </div>
      )}

      {zaznam.description && (
        <section className="mt-6">
          <p className="text-bloom-sub leading-relaxed whitespace-pre-line">{zaznam.description}</p>
        </section>
      )}

      {kind === 'resources' && d.body && (
        <section className="mt-6 bg-white border border-border/40 rounded-2xl p-5">
          <p className="text-bloom-text leading-relaxed whitespace-pre-line">{d.body}</p>
        </section>
      )}

      {/* Co je zvláštní pro daný druh */}
      <div className="mt-6 grid gap-4 sm:grid-cols-2">
        {seznamPole(t('detail.oblasti'), d.areas)}
        {seznamPole(t('detail.coNabizi'), d.services)}
        {seznamPole(t('detail.kategorie'), zaznam.categories)}
        {seznamPole(t('detail.jazyky'), zaznam.languages)}
        {seznamPole(t('detail.vitezove'), d.previous_winners)}
        {d.eligibility && textPole(t('detail.kdoSeMuze'), d.eligibility)}
        {d.organizer_name && textPole(t('detail.poradatel'), d.organizer_name)}
        {d.fee && textPole(t('detail.poplatek'), d.fee)}
        {d.topic && textPole(t('detail.tema'), d.topic)}
      </div>

      {/* Kontakty */}
      {(zaznam.location?.address || zaznam.email || zaznam.phone || odkazy.length > 0
        || d.url || d.registration_url || (d.links || []).length > 0) && (
        <section className="mt-8 bg-white border border-border/40 rounded-2xl p-5">
          <h2 className="font-heading font-bold text-bloom-text">{t('detail.kontakty')}</h2>
          <div className="mt-3 space-y-2.5 text-sm">
            {zaznam.location?.address && (
              <p className="flex items-start gap-2 text-bloom-sub">
                <MapPin className="w-4 h-4 text-bloom-violet mt-0.5 shrink-0" />
                {zaznam.location.address}
              </p>
            )}
            {zaznam.email && (
              <p className="flex items-center gap-2">
                <Mail className="w-4 h-4 text-bloom-violet shrink-0" />
                <a href={`mailto:${zaznam.email}`} className="text-bloom-violet hover:underline">{zaznam.email}</a>
              </p>
            )}
            {zaznam.phone && (
              <p className="flex items-center gap-2">
                <Phone className="w-4 h-4 text-bloom-violet shrink-0" />
                <a href={`tel:${zaznam.phone}`} className="text-bloom-violet hover:underline">{zaznam.phone}</a>
              </p>
            )}
            {d.registration_url && <Odkaz url={d.registration_url} popisek={t('detail.prihlaska')} />}
            {d.url && <Odkaz url={d.url} popisek={t('detail.strankaAkce')} />}
            {odkazy.map(([klic, url, Icon, popisek]) => (
              <Odkaz key={klic} url={url} popisek={popisek} Icon={Icon} />
            ))}
            {(d.links || []).map((url) => <Odkaz key={url} url={url} popisek={url} />)}
          </div>
        </section>
      )}

      <ClaimProfile kind={kind} zaznam={zaznam} onZmena={setZaznam} />

      {/* Poctivá patička: odkud to je a co ověření znamená */}
      <footer className="mt-8 pt-5 border-t border-border/40 text-xs text-bloom-sub/80 space-y-1">
        {zaznam.source && <p>{t('detail.zdroj', { zdroj: zaznam.source })}</p>}
        {zaznam.updated_at && (
          <p>{t('detail.upraveno', { datum: datum(zaznam.updated_at, false, jazyk) })}</p>
        )}
        <p>
          {zaznam.verification === 'verified'
            ? t('detail.overeno')
            : t('detail.neovereno')}
        </p>
      </footer>
    </div>
  );
}

function Udaj({ icon: Icon, popisek, hodnota }) {
  return (
    <div className="bg-white border border-border/40 rounded-2xl p-4">
      <p className="text-xs text-bloom-sub flex items-center gap-1.5">
        <Icon className="w-3.5 h-3.5 text-bloom-violet" />{popisek}
      </p>
      <p className="mt-1 font-medium text-bloom-text">{hodnota}</p>
    </div>
  );
}

function seznamPole(popisek, hodnoty) {
  if (!hodnoty || hodnoty.length === 0) return null;
  return (
    <div key={popisek}>
      <p className="text-xs text-bloom-sub">{popisek}</p>
      <div className="mt-1.5 flex flex-wrap gap-1.5">
        {hodnoty.map((h) => (
          <span key={h} className="text-xs bg-bloom-violet/10 text-bloom-violet rounded-lg px-2 py-1">{h}</span>
        ))}
      </div>
    </div>
  );
}

function textPole(popisek, hodnota) {
  return (
    <div key={popisek}>
      <p className="text-xs text-bloom-sub">{popisek}</p>
      <p className="mt-1 text-sm text-bloom-text">{hodnota}</p>
    </div>
  );
}

function Odkaz({ url, popisek, Icon = ExternalLink }) {
  const cela = /^https?:\/\//i.test(url) ? url : `https://${url}`;
  return (
    <p className="flex items-center gap-2">
      <Icon className="w-4 h-4 text-bloom-violet shrink-0" />
      <a href={cela} target="_blank" rel="noopener noreferrer"
         className="text-bloom-violet hover:underline break-all">{popisek}</a>
    </p>
  );
}
